import axios, { AxiosError, AxiosResponse } from 'axios';
import * as R from 'ramda';
import { TechnicalError } from '../../../common/errors';
import Log from '../../../common/logging';
import RetryHandler from './retryHandler';
import * as retryUtils from './utils';

// HTTP errors codes where retry should not be performed
const DoNotRetryErrorCodes = [401, 403, 404, 405, 406, 412, 429];

const retryHandlers: RetryHandler[] = [];
export const registerRetryHandler = (handler: RetryHandler): void => {
  retryHandlers.push(handler);
};

export default function setupNetworkRetries(): void {
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError): Promise<AxiosResponse> => {
      if (R.contains(error.response?.status, DoNotRetryErrorCodes)) {
        return Promise.reject(error);
      }

      // If the error response has business errors, then return as a successful result with the error code
      if (error.response && retryUtils.responseHasValidationErrors(error.response)) {
        return Promise.resolve(error.response);
      }

      const { retryConfig } = error.config;
      // Fail out if no retry configuration is specified
      if (!retryConfig) {
        return Promise.reject(error);
      }

      if (retryUtils.shouldRetryAutomatically(retryConfig)) {
        await retryUtils.delayRetry(retryConfig);
        const updatedConfig = retryUtils.updateConfigForAutomaticRetry(retryConfig);
        return retryUtils.performRetry(retryHandlers, {
          ...error,
          config: { ...error.config, retryConfig: updatedConfig },
        });
      }

      if (retryUtils.shouldPromptUser(retryConfig) && (await retryUtils.showUserErrorPrompt(retryConfig)) === 'retry') {
        const updatedConfig = retryUtils.updateConfigForUserRetry(retryConfig);
        return retryUtils.performRetry(retryHandlers, {
          ...error,
          config: { ...error.config, retryConfig: updatedConfig },
        });
      }

      const finalError = new TechnicalError('Exhausted user retries after network failure', error);
      Log.error('Axios interceptor error', finalError);

      return Promise.reject(error);
    },
  );
}
